import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * `/portal-login/:token` — landing route for client magic-link emails.
 *
 * Exchanges the URL token for a JWT pair via the backend, stows it via
 * the existing AuthContext, then redirects to the client portal home
 * (or to a `?next=` URL when the email was targeting a specific screen
 * — e.g. the onboarding wizard invites clients straight to their
 * 5-step wizard, not the portal dashboard).
 */
export default function MagicLinkLandingPage() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [status, setStatus] = useState("checking");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/client/magic-link/${encodeURIComponent(token)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          if (cancelled) return;
          setErrorMessage(data.detail || "This sign-in link is no longer valid.");
          setStatus("error");
          return;
        }
        const data = await resp.json();
        if (cancelled) return;
        if (typeof applySession === "function") {
          applySession(data);
        } else {
          localStorage.setItem("access_token", data.access_token);
          localStorage.setItem("refresh_token", data.refresh_token);
          localStorage.setItem("user", JSON.stringify(data.user));
        }
        setStatus("ok");
        // Only accept same-origin relative paths so a malicious link
        // can't bounce the user to an external site after login.
        const raw = search.get("next");
        const next = raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
        navigate(next, { replace: true });
      } catch (e) {
        if (cancelled) return;
        setErrorMessage("Couldn't validate the sign-in link. Please try again.");
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate, applySession, search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center" data-testid="magic-link-landing">
        {status === "checking" && (
          <>
            <div className="w-10 h-10 mx-auto mb-4 border-2 border-slate-200 border-t-[#100f5d] rounded-full animate-spin" />
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Signing you in…</h1>
            <p className="text-sm text-slate-500 mt-1">Just a moment.</p>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Sign-in link expired or invalid</h1>
            <p className="text-sm text-slate-500 mt-1" data-testid="magic-link-error">{errorMessage}</p>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="mt-4 text-sm font-semibold hover:underline"
              style={{ color: "#100f5d" }}
              data-testid="magic-link-back-to-login"
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
